import { useMemo } from 'react';
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter';
import type { TypeFilterValue } from '@inovua/reactdatagrid-community/types';
import { Trans, useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { uniqBy } from 'lodash';

import type { DocumentDetailsItem } from '@/api/domains/reconciliation.types';
import { LicenseHolderAndMakerInfo } from '@/components/LicenseHolderAndMakerInfo';
import { createSortFunction, DataGrid, useDataGridSorting, type DataGridColumn } from '@/components/ReactDataGrid';
import { formatDateTime, formatNumberByThousands } from '@/shared/helpers/format.helpers';

const MISMATCH_TEXT_COLOR = '#FF4D4F';
const MISMATCH_ROW_BG = 'rgba(255, 77, 79, 0.08)';

type ReconciliationResultDataGridProps = {
  items: DocumentDetailsItem[];
  isLoading?: boolean;
  isFiltersVisible?: boolean;
};

const isUnmatched = (item: DocumentDetailsItem): boolean => !item.order_position || !item.drug_id;

const getPaymentType = (item: DocumentDetailsItem): string => {
  if (!item.order_position) return '—';

  const { payment_percent, payment_delay } = item.order_position;

  if (payment_percent === 0 && payment_delay === 0) return 'Договорная';
  if (payment_percent > 0 && payment_delay > 0) return `${payment_percent}% / ${payment_delay} дн.`;
  if (payment_percent > 0) return `${payment_percent}%`;
  if (payment_delay > 0) return `${payment_delay} дн.`;

  return 'Договорная';
};

type ProcessedItem = DocumentDetailsItem & {
  payment_type: string;
  expire_date_order: string;
  count_order: number;
  price_order: number;
};

const processItems = (items: DocumentDetailsItem[]): ProcessedItem[] =>
  items.map(item => ({
    ...item,
    payment_type: getPaymentType(item),
    expire_date_order: item.order_position?.expire_date ?? '',
    count_order: item.order_position?.count ?? 0,
    price_order: item.order_position?.origin_price_with_vat ?? 0,
  }));

const sortReconciliationResult = createSortFunction<ProcessedItem>({
  drug_name: sortBy => sortBy.string(drug_name => drug_name),
  payment_type: sortBy => sortBy.string(payment_type => payment_type),
  expire_date_order: sortBy => sortBy.string(expire_date_order => expire_date_order),
  expire_date: sortBy => sortBy.string(expire_date => expire_date),
  count_order: sortBy => sortBy.number(count_order => count_order),
  count: sortBy => sortBy.number(count => count),
  price_order: sortBy => sortBy.number(price_order => price_order),
  price_with_vat: sortBy => sortBy.number(price_with_vat => price_with_vat),
});

const defaultFilterValue: TypeFilterValue = [
  { name: 'drug_name', type: 'select', operator: 'inlist', value: null },
  { name: 'payment_type', type: 'select', operator: 'inlist', value: null },
  { name: 'expire_date_order', type: 'select', operator: 'inlist', value: null },
  { name: 'expire_date', type: 'select', operator: 'inlist', value: null },
  { name: 'count_order', type: 'select', operator: 'inlist', value: null },
  { name: 'count', type: 'select', operator: 'inlist', value: null },
  { name: 'price_order', type: 'select', operator: 'inlist', value: null },
  { name: 'price_with_vat', type: 'select', operator: 'inlist', value: null },
];

const SELECT_FILTER_PROPS = {
  multiple: true,
  wrapMultiple: false,
  placeholder: 'Выбрать',
};

const toFilterDataSource = (values: Array<{ id: string | number; label: string }>) => uniqBy(values, 'id');

export const ReconciliationResultDataGrid = ({
  items,
  isLoading,
  isFiltersVisible = false,
}: ReconciliationResultDataGridProps) => {
  const { t } = useTranslation('orders-page');

  const processedItems = useMemo(() => processItems(items), [items]);
  const { dataSource, sortInfo, handleSortInfoChange } = useDataGridSorting(processedItems, sortReconciliationResult);

  const columns: Array<DataGridColumn<ProcessedItem>> = useMemo(
    () => [
      {
        id: 'row-number',
        header: '№',
        defaultWidth: 60,
        type: 'number',
        sortable: false,
        render: ({ rowIndex }: { rowIndex: number }) => rowIndex + 1,
      },
      {
        name: 'drug_name',
        header: t('reconciliation.result.grid.drug-name'),
        defaultFlex: 2,
        minWidth: 250,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(
            items.map(item => {
              const name = item.order_position?.drug_full_name ?? item.drug_name;
              return { id: name, label: name };
            }),
          ),
        },
        render: ({ data }: { data: ProcessedItem }) => {
          const maker = data.order_position
            ? { name: data.order_position.maker_name, country: data.order_position.maker_country_name }
            : (data.drug?.maker ?? { name: data.maker_name, country: '' });

          const licenseHolder = data.order_position
            ? {
                name: data.order_position.license_holder_name,
                country: data.order_position.license_holder_country_name,
              }
            : (data.drug?.license_holder ?? null);

          return (
            <Stack>
              <Typography variant="Body/large">{data.order_position?.drug_full_name ?? data.drug_name}</Typography>
              <LicenseHolderAndMakerInfo licenseHolder={licenseHolder} maker={maker} placement="right" size="small">
                <Typography variant="Body/large" color="text.secondary">
                  {data.order_position
                    ? `${data.order_position.maker_name} (${data.order_position.maker_country_name})`
                    : data.maker_name}
                </Typography>
              </LicenseHolderAndMakerInfo>
            </Stack>
          );
        },
      },
      {
        name: 'payment_type',
        header: t('reconciliation.result.grid.payment-type'),
        defaultWidth: 130,
        textAlign: 'end' as const,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(
            items.map(item => {
              const val = getPaymentType(item);
              return { id: val, label: val };
            }),
          ),
        },
        render: ({ data }: { data: ProcessedItem }) => data.payment_type,
      },
      {
        name: 'expire_date_order',
        header: <Trans ns="orders-page" i18nKey="reconciliation.result.grid.expire-date-order" />,
        defaultWidth: 130,
        textAlign: 'end' as const,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(
            items.map(item => ({
              id: item.order_position?.expire_date ?? '',
              label: item.order_position?.expire_date
                ? formatDateTime(item.order_position.expire_date, 'dd.MM.yyyy')
                : '—',
            })),
          ),
        },
        render: ({ data }: { data: ProcessedItem }) => (
          <Typography variant="Body/large">
            {data.order_position?.expire_date ? formatDateTime(data.order_position.expire_date, 'dd.MM.yyyy') : '—'}
          </Typography>
        ),
      },
      {
        name: 'expire_date',
        header: <Trans ns="orders-page" i18nKey="reconciliation.result.grid.expire-date-doc" />,
        defaultWidth: 130,
        textAlign: 'end' as const,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(
            items.map(item => {
              const isEmptyDate = (date: string | null | undefined) => !date || new Date(date).getFullYear() <= 1;
              return {
                id: item.expire_date ?? '',
                label: !isEmptyDate(item.expire_date) ? formatDateTime(item.expire_date, 'dd.MM.yyyy') : '—',
              };
            }),
          ),
        },
        render: ({ data }: { data: ProcessedItem }) => {
          const isEmptyDate = (date: string | null | undefined) => !date || new Date(date).getFullYear() <= 1;
          const docDate = !isEmptyDate(data.expire_date) ? formatDateTime(data.expire_date, 'dd.MM.yyyy') : null;
          const orderExpireDate = data.order_position?.expire_date ?? null;
          const orderDate =
            orderExpireDate && !isEmptyDate(orderExpireDate) ? formatDateTime(orderExpireDate, 'dd.MM.yyyy') : null;
          const mismatch = data.order_position && docDate !== orderDate;

          return (
            <Typography variant="Body/large" sx={{ color: mismatch ? MISMATCH_TEXT_COLOR : undefined }}>
              {docDate ?? '—'}
            </Typography>
          );
        },
      },
      {
        name: 'count_order',
        header: <Trans ns="orders-page" i18nKey="reconciliation.result.grid.quantity-order" />,
        defaultWidth: 110,
        type: 'number',
        textAlign: 'end' as const,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(
            items.map(item => ({
              id: item.order_position?.count ?? 0,
              label: String(item.order_position?.count ?? '—'),
            })),
          ),
        },
        render: ({ data }: { data: ProcessedItem }) => (
          <Typography variant="Body/large">{data.order_position?.count ?? '—'}</Typography>
        ),
      },
      {
        name: 'count',
        header: <Trans ns="orders-page" i18nKey="reconciliation.result.grid.quantity-doc" />,
        defaultWidth: 110,
        type: 'number',
        textAlign: 'end' as const,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(items.map(item => ({ id: item.count, label: String(item.count) }))),
        },
        render: ({ data }: { data: ProcessedItem }) => {
          const mismatch = data.order_position && data.count !== data.order_position.count;

          return (
            <Typography variant="Body/large" sx={{ color: mismatch ? MISMATCH_TEXT_COLOR : undefined }}>
              {data.count}
            </Typography>
          );
        },
      },
      {
        name: 'price_order',
        header: t('reconciliation.result.grid.price-order'),
        defaultWidth: 150,
        type: 'number',
        textAlign: 'end' as const,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(
            items.map(item => ({
              id: item.order_position?.origin_price_with_vat ?? 0,
              label: item.order_position ? formatNumberByThousands(item.order_position.origin_price_with_vat) : '—',
            })),
          ),
        },
        render: ({ data }: { data: ProcessedItem }) => (
          <Typography variant="Body/large">
            {data.order_position ? formatNumberByThousands(data.order_position.origin_price_with_vat) : '—'}
          </Typography>
        ),
      },
      {
        name: 'price_with_vat',
        header: t('reconciliation.result.grid.price-doc'),
        defaultWidth: 150,
        type: 'number',
        textAlign: 'end' as const,
        filterEditor: SelectFilter,
        filterEditorProps: {
          ...SELECT_FILTER_PROPS,
          dataSource: toFilterDataSource(
            items.map(item => ({
              id: item.price_with_vat,
              label: formatNumberByThousands(item.price_with_vat),
            })),
          ),
        },
        render: ({ data }: { data: ProcessedItem }) => {
          const mismatch = data.order_position && data.price_with_vat !== data.order_position.origin_price_with_vat;

          return (
            <Typography variant="Body/large" sx={{ color: mismatch ? MISMATCH_TEXT_COLOR : undefined }}>
              {formatNumberByThousands(data.price_with_vat)}
            </Typography>
          );
        },
      },
    ],
    [t, items],
  );

  const rowStyle = ({ data }: { data: ProcessedItem }) => {
    if (isUnmatched(data)) {
      return { background: MISMATCH_ROW_BG };
    }
    return undefined;
  };

  return (
    <DataGrid
      idProperty="id"
      columns={columns}
      dataSource={dataSource}
      loading={isLoading}
      rowStyle={rowStyle}
      sortInfo={sortInfo}
      onSortInfoChange={handleSortInfoChange}
      enableSelection={false}
      enableFiltering={isFiltersVisible}
      enableColumnFilterContextMenu={false}
      defaultFilterValue={defaultFilterValue}
      rowHeight={56}
    />
  );
};
